import {
  Button,
  Container,
  Stack,
  Table,
  TableCaption,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { flexRender } from '@tanstack/react-table';
import React from 'react';
import { downloadCSV } from 'utils/fileGeneration';
import { graphql, useFragment, useSafeRelay } from 'utils/graphClient';
import { EmptyResponsesAlert } from './components/EmptyResponsesAlert';
import { useFormResponsesTable } from './useFormResponsesTable';
import { FormResponses_content$key } from './__generated__/FormResponses_content.graphql';
import { FormResponsesQuery } from './__generated__/FormResponsesQuery.graphql';

const fragment = graphql`
  fragment FormResponses_content on FolderItemContent {
    ... on Form {
      title
      isClosed
      questions {
        id
        text
        definition {
          __typename
          ... on CustomField__Checkboxes {
            id
            options {
              value
              text
            }
          }
          ... on CustomField__Numeric {
            id
          }
          ... on CustomField__String {
            id
          }
          ... on CustomField__MultipleChoice {
            id
            options {
              value
              text
            }
          }
          ... on CustomField__Date {
            id
          }
          ... on CustomField__Time {
            id
          }
          ... on CustomField__DateTime {
            id
          }
          ... on CustomField__Email {
            id
          }
          ... on CustomField__PhoneNumber {
            id
          }
          ... on CustomField__Link {
            id
          }
        }
      }
      responses {
        edges {
          node {
            id
            respondedAt
            state
            responder {
              ... on Member {
                name
              }
            }
            answers {
              __typename
              id
              questionId
              value {
                __typename
                ... on CustomValue__Checkboxes {
                  checkboxValues: value
                }
                ... on CustomValue__String {
                  stringValue: value
                }
                ... on CustomValue__Numeric {
                  numericValue: value
                }
                ... on CustomValue__MultipleChoice {
                  multipleChoiceValue: value
                }
                ... on CustomValue__Date {
                  dateValue: value
                }
                ... on CustomValue__Time {
                  timeValue: value
                }
                ... on CustomValue__DateTime {
                  dateTimeValue: value
                }
                ... on CustomValue__Email {
                  emailValue: value
                }
                ... on CustomValue__PhoneNumber {
                  phoneNumberValue: value
                }
                ... on CustomValue__Link {
                  linkValue: value
                }
              }
            }
          }
        }
      }
    }
  }
`;

interface FormResponsesProps {
  nodeId: string;
}

export const FormResponses: React.FC<FormResponsesProps> = ({ nodeId }) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const queryResponse = useLazyLoadQuery<FormResponsesQuery>(
    graphql`
      query FormResponsesQuery($id: ID!) {
        node(id: $id) {
          ... on FolderItem {
            content {
              ...FormResponses_content
            }
          }
        }
      }
    `,
    { id: nodeId },
  );

  if (!queryResponse.node) {
    throw new Error('Cannot load responses for Form');
  }

  const { questions, responses, title, isClosed } = useFragment(
    fragment,
    queryResponse.node.content as FormResponses_content$key,
  );

  const { getHeaderGroups, getRowModel, columns, data } = useFormResponsesTable(
    {
      questions,
      responses,
    },
  );

  const handleGenerateCSV = () => {
    const values = data.map((row) =>
      (columns as { accessorKey: string }[]).map(
        (col: { accessorKey: string }) => row[col.accessorKey],
      ),
    );

    // This method delays the loading of the actual CSV logic, it is safe to use inline
    downloadCSV({
      filename: `${title}.csv`,
      rows: [columns.map((col) => col.header as string), ...values],
    });
  };

  if (responses?.edges.length === 0) {
    return (
      <Container maxW="3xl">
        <EmptyResponsesAlert isClosed={!!isClosed} />
      </Container>
    );
  }

  return (
    <Stack h="full" overflowY="scroll">
      <Button
        onClick={handleGenerateCSV}
        alignSelf="flex-end"
        variant="outline"
        isDisabled={responses?.edges.length === 0}
      >
        {t`Export CSV`}
      </Button>

      <TableContainer overflowY="auto">
        <Table variant="striped">
          <TableCaption>{t`Form Responses`}</TableCaption>

          <Thead>
            {getHeaderGroups().map((headerGroup) => {
              return (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      cursor={
                        header.column.getCanSort() ? 'pointer' : 'default'
                      }
                      userSelect="none"
                      textTransform="none"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                      {{
                        asc: ' ▲',
                        desc: ' ▼',
                      }[header.column.getIsSorted() as string] ?? null}
                    </Th>
                  ))}
                </Tr>
              );
            })}
          </Thead>

          <Tbody>
            {getRowModel().rows.map((row) => {
              return (
                <Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <Td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Stack>
  );
};
